import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { apiPost } from '../lib/api.ts'

/**
 * The browser half of the WebAuthn ceremonies (§6c).
 *
 * @simplewebauthn/browser does the encoding, which is the whole job on this
 * side: `navigator.credentials` speaks ArrayBuffers and the wire speaks
 * base64url, and getting that conversion subtly wrong produces a signature that
 * fails to verify with no useful error. Its server counterpart is the library
 * §6c names, and mixing one with a hand-rolled other is how the two stop
 * agreeing about encoding.
 */

/** True when this browser can do WebAuthn at all. */
export function webauthnAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function'
}

export class WebauthnCancelled extends Error {
  constructor() {
    super('cancelled')
    this.name = 'WebauthnCancelled'
  }
}

/**
 * A user closing the browser's key prompt is not an error to report.
 *
 * The DOM raises NotAllowedError for both "the user dismissed this" and "the
 * key you presented is not one of the allowed credentials", which is
 * deliberate on the platform's part — distinguishing them would let a page
 * probe which keys someone is carrying. So the calling screen treats it as
 * "nothing happened" rather than as a failure worth a red message.
 */
function isCancellation(error: unknown): boolean {
  return error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
}

export async function registerSecurityKey(label: string): Promise<void> {
  const options = await apiPost<Record<string, unknown>>('/auth/mfa/webauthn/register/begin')
  let response
  try {
    response = await startRegistration({ optionsJSON: options as never })
  } catch (error) {
    if (isCancellation(error)) throw new WebauthnCancelled()
    throw error
  }
  await apiPost('/auth/mfa/webauthn/register/finish', { response, label })
}

export async function presentSecurityKey(): Promise<void> {
  const options = await apiPost<Record<string, unknown>>('/auth/mfa/webauthn/begin')
  let response
  try {
    response = await startAuthentication({ optionsJSON: options as never })
  } catch (error) {
    if (isCancellation(error)) throw new WebauthnCancelled()
    throw error
  }
  await apiPost('/auth/mfa/webauthn/finish', { response })
}
